import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { API_BASE_URL } from './api-base';

@Injectable({
  providedIn: 'root'
})
export class ControlhelpService {
  private apiUrl = `${API_BASE_URL}/controlhelp`;

  constructor(private http: HttpClient) {}

  getTickets(tenantId: number, filters?: { status?: string; priority?: string; search?: string; project_id?: number }): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (filters?.status && filters.status !== 'ALL') {
      params = params.set('status', filters.status);
    }
    if (filters?.priority && filters.priority !== 'ALL') {
      params = params.set('priority', filters.priority);
    }
    if (filters?.search) {
      params = params.set('search', filters.search);
    }
    if (filters?.project_id) {
      params = params.set('project_id', filters.project_id);
    }
    return this.http.get<any[]>(`${this.apiUrl}/tickets`, { params });
  }

  createTicket(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/tickets`, payload);
  }

  updateTicket(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/tickets/${id}`, payload);
  }

  deleteTicket(ticketId: number, payload: { tenant_id: number; user_id: number; project_id?: number | null }): Observable<any> {
    const params = new HttpParams().set('tenant_id', payload.tenant_id).set('user_id', payload.user_id);
    if (payload.project_id) {
      return this.http.delete(`${this.apiUrl}/tickets/${ticketId}`, { params: params.set('project_id', payload.project_id) });
    }
    return this.http.delete(`${this.apiUrl}/tickets/${ticketId}`, { params });
  }

  getComments(tenantId: number, ticketId: number, userId?: number | null): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http
      .get<any[]>(`${this.apiUrl}/tickets/${ticketId}/comments`, { params })
      .pipe(timeout(15000), catchError(() => of([])));
  }

  addComment(ticketId: number, payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/tickets/${ticketId}/comments`, payload);
  }

  getAttachments(tenantId: number, ticketId: number, userId?: number | null): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http
      .get<any[]>(`${this.apiUrl}/tickets/${ticketId}/attachments`, { params })
      .pipe(timeout(15000), catchError(() => of([])));
  }

  getHistory(tenantId: number, ticketId: number, userId?: number | null): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http
      .get<any[]>(`${this.apiUrl}/tickets/${ticketId}/history`, { params })
      .pipe(timeout(15000), catchError(() => of([])));
  }

  getWatchers(tenantId: number, ticketId: number): Observable<any[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http
      .get<any[]>(`${this.apiUrl}/tickets/${ticketId}/watchers`, { params })
      .pipe(timeout(15000), catchError(() => of([])));
  }

  setWatchers(ticketId: number, payload: any): Observable<any[]> {
    return this.http.put<any[]>(`${this.apiUrl}/tickets/${ticketId}/watchers`, payload);
  }

  uploadAttachment(ticketId: number, formData: FormData, tenantId?: number): Observable<any> {
    const params = tenantId ? new HttpParams().set('tenant_id', tenantId) : undefined;
    const options = params ? { params } : undefined;
    return this.http.post(`${this.apiUrl}/tickets/${ticketId}/attachments`, formData, options);
  }

  getCategories(tenantId: number, departmentId?: number | null): Observable<any[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    const finalParams = departmentId ? params.set('department_id', departmentId) : params;
    return this.http.get<any[]>(`${this.apiUrl}/categories`, { params: finalParams });
  }

  getUpdates(
    tenantId: number,
    userId: number,
    departmentId?: number | null,
    since?: string | null
  ): Observable<{ has_updates: boolean; count: number }> {
    let params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    if (departmentId) {
      params = params.set('department_id', departmentId);
    }
    if (since) {
      params = params.set('since', since);
    }
    return this.http.get<{ has_updates: boolean; count: number }>(`${this.apiUrl}/updates`, { params });
  }

  getOpenCount(
    tenantId: number,
    userId: number,
    departmentIds?: number[] | number | null
  ): Observable<{ open_count: number }> {
    let params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    if (Array.isArray(departmentIds) && departmentIds.length) {
      params = params.set('department_ids', departmentIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)).join(','));
    } else if (departmentIds) {
      params = params.set('department_id', Number(departmentIds));
    }
    return this.http.get<{ open_count: number }>(`${this.apiUrl}/open-count`, { params });
  }

  getMyRequestsCount(tenantId: number, userId: number): Observable<{ open_count: number }> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<{ open_count: number }>(`${this.apiUrl}/my-requests-count`, { params });
  }

  createCategory(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/categories`, payload);
  }

  updateCategory(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/categories/${id}`, payload);
  }

  getSla(tenantId: number): Observable<any[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.apiUrl}/sla`, { params });
  }

  createSla(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/sla`, payload);
  }

  updateSla(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/sla/${id}`, payload);
  }
}
